<template>
  <v-container fluid class="pb-6 pt-4">
    <v-row justify="end" dense class="mr-6">
        <Icon
          v-if="allowDenseCharts"
          :icon="denseCharts ? 'mdi-grid-large' : 'mdi-grid'"
          iconColor="primary"
          :tooltipText="denseCharts ? $t('displayExpanded') : $t('displayDense')"
          :small="false"
          margin="mt-4 mr-3"
          @icon-clicked="toggleDenseCharts"
        />
        <OperationalWidgetPicker
          :propWidgetSet="widgetSet"
          :openMenuKey="widgetPickerMenuKey"
          @new-widget-layout="newWidgetLayout"
        />
    </v-row>
    <v-row
      v-if="widgetSlotSet.widgetSlots.length === 0"
      style="opacity: 0.2;"
    >
      <div style="text-align: center; width: 100%; padding-top: min(48px, 10vh);">
        <v-icon
          size="40vmin"
          @click="addNewWidget"
        >
          mdi-chart-box-plus-outline
        </v-icon>
        <br>
        <span
          class="title"
          @click="addNewWidget"
          style="user-select: none;"
        >
          {{$t('clickToAddAWidget')}}
        </span>
      </div>
    </v-row>
    <GridContainer
      v-if="widgetSlotSet.widgetSlots.length"
      :rows="containerRows"
      :columns="containerColumns"
      flow="row"
      gap="20px"
      class="py-4"
    >
      <GridArea
        v-for="widget, idx in widgetSlotSet.widgetSlots"
        :key="`widget-id-${widget.id !== 'empty' ? widget.id : widget.id.concat(idx)}`"
        :area="widget.dimensions"
        :id="widget.id"
      >
        <StatsCard
        v-if="widget.id === 'incompleteTickets'"
        @click="onDataClicked(DashboardByproductOperationalWidgets.IncompleteTickets, widget.details?.detailedWidget, undefined, undefined, incompleteTickets)"
        interactive
        icon="mdi-receipt-text-clock-outline"
        color="secondary"
        :title="$t('incompleteTickets')"
        :content="incompleteTickets.count"
        :loading="incompleteTickets?.count === undefined"/>

        <LoadableComponent v-if="widget.id === 'loadsInTransitByDestination'" :loading="!loadsInTransitByDestinationChart" type="dashboard-widget">
          <StackedBarChartCardForByproductLoadsByDestination
            type="LoadsInTransitByDestination"
            :overrideTitle="$t('loadsInTransitByDestination')"
            :data="loadsInTransitByDestinationChart"
            elementId="in-transit-loads-by-external-destination-and-from-account-chart"
            :tooltip="$t('loadsInTransitTooltip')"
            isInteractive
            @data-clicked="onDataClicked(DashboardByproductOperationalWidgets.LoadsInTransitByDestination, widget.details?.detailedWidget, 'destination', 'fromAccount', loadsInTransitByDestinationChart, $event)"
            @refresh="refreshChartData('LoadsInTransitByDestination')"
            :contractMode="ContractMode.Byproducts"
          />
        </LoadableComponent>

        <LoadableComponent v-if="widget.id === 'loadsReadyForPickup'" :loading="!ticketsReadyForPickupChart" type="dashboard-widget">
          <StackedBarChartCardForTicketsForPickup
            type="TicketsReadyForPickup"
            :data="ticketsReadyForPickupChart"
            elementId="tickets-ready-for-pickup-chart"
            isInteractive
            @data-clicked="onDataClicked(DashboardByproductOperationalWidgets.LoadsReadyForPickup, widget.details?.detailedWidget, 'fromAccount', 'product', ticketsReadyForPickupChart, $event)"
            @refresh="options => refreshChartData('TicketsReadyForPickup', options)"
            :isByproduct="true"
          />
        </LoadableComponent>
      </GridArea>
    </GridContainer>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ByproductOperationalDashboardWidgetSet } from '@/model/DashboardWidget.js'
import {
  StackedBarChartForByproductLoadsInTransitByDestination,
  StackedBarChartForBPTicketsReadyForPickup
} from '@/model/StackedBarChart.js'
import { WidgetSlot, WidgetSlotSet } from '@/model/WidgetSlot.js'
import { DashboardByproductOperationalWidgets, byproductOperationalWidgetMappings } from '@/components/dashboard/WidgetMappings'
import { getNumEmptySlots } from '@/utils/DashboardHelpers'
import { ContractType, ContractMode } from '@/utils/Enumerations.js'
import { chartArea } from '@/components/dashboard/GridHelpers'

export default {
  name: 'ByproductOperationalSummary',

  components: {
    GridArea: () => import('@/components/helper/GridArea.vue'),
    GridContainer: () => import('@/components/helper/GridContainer.vue'),
    StackedBarChartCardForTicketsForPickup: () => import('@/components/dashboard/StackedBarChartCardForTicketsForPickup.vue'),
    OperationalWidgetPicker: () => import('@/components/dashboard/OperationalWidgetPicker.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    LoadableComponent: () => import('../helper/LoadableComponent.vue'),
    StatsCard: () => import('./StatsCard.vue'),
    StackedBarChartCardForByproductLoadsByDestination: () => import('@/components/dashboard/StackedBarChartCardForByproductLoadsByDestination.vue')
  },

  data: () => ({
    DashboardByproductOperationalWidgets,
    containerRows: { sm: '60px', md: 'calc(45px + 0.5vw)' },
    containerColumns: { sm: 'repeat(12, 1fr)', md: 'repeat(24, 1fr)' },
    ticketsReadyForPickup: undefined,
    loadsInTransitByDestination: undefined,
    widgetSet: undefined,
    denseCharts: false,
    widgetPickerMenuKey: 0,
    readyForPickupFilter: { limitToUnreserved: false },
    ContractMode
  }),

  props: {
    allowDenseCharts: {
      type: Boolean,
      default: true,
      required: false
    },

    driverDataStale: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    async driverDataStale (v) {
      if (!v) return
      await this.fetchIncompleteTickets([
        ContractType.ByproductSale.value,
        ContractType.ByproductPurchase.value,
        ContractType.ByproductHauling.value
      ])
      if (this.readyForPickupFilter.limitToUnreserved) this.ticketsReadyForPickup = await this.fetchByproductTicketsReadyForPickup(this.readyForPickupFilter)
      this.$emit('driverDataUpdated')
    }
  },

  computed: {
    ...mapGetters('dashboard', ['incompleteTickets']),

    widgetSlotSet () {
      const selectedWidgets = this.widgetSet.widgets
        .filter(c => c.visible)
        .map(c => {
          const mapping = this.widgetMappingByWidgetId(c.text)
          return new WidgetSlot(
            c.text,
            this.isCard(c) ? this.cardArea : this.chartArea,
            {
              widgetType: c.widgetType,
              detailedWidget: mapping
            }
          )
        })

      if (selectedWidgets.some(c => this.isCard(c))) {
        const numCards = selectedWidgets.filter(c => this.isCard(c)).length
        if (getNumEmptySlots(numCards) > 0) {
          const li = selectedWidgets.findLastIndex(c => this.isCard(c))
          return new WidgetSlotSet([
            ...selectedWidgets.slice(0, li + 1),
            ...new Array(getNumEmptySlots(numCards)).fill(new WidgetSlot('empty', this.cardArea)),
            ...selectedWidgets.slice(li + 1)
          ])
        }
      }
      return new WidgetSlotSet(selectedWidgets)
    },

    loadsInTransitByDestinationChart () {
      return (this.loadsInTransitByDestination)
        ? new StackedBarChartForByproductLoadsInTransitByDestination(this.loadsInTransitByDestination, 'destinationName', 'fromAccountName')
        : undefined
    },

    ticketsReadyForPickupChart () {
      return (this.ticketsReadyForPickup)
        ? new StackedBarChartForBPTicketsReadyForPickup(this.ticketsReadyForPickup, 'fromAccount', 'fromAccount')
        : undefined
    },

    chartArea () {
      return chartArea(this.denseCharts)
    },

    cardArea () {
      return [2, {
        lg: 12, xl: 8
      }]
    }
  },

  created () {
    this.widgetSet = new ByproductOperationalDashboardWidgetSet()
    this.fetchWidgetData()
  },

  methods: {
    ...mapActions('dashboard', [
      'fetchByproductLoadsInTransitByDestination',
      'fetchByproductTicketsReadyForPickup',
      'fetchIncompleteTickets'
    ]),

    newWidgetLayout (widgetSet) {
      this.fetchWidgetData(widgetSet)
    },

    async fetchWidgetData (widgetSet = this.widgetSet) {
      const loadsInTransitByDestinationWidgets = [0]
      const ticketsReadyForPickupWidgets = [1]
      const incompleteTickets = [2]

      if (widgetSet.someWidgetShowing(loadsInTransitByDestinationWidgets)) {
        this.loadsInTransitByDestination = await this.fetchByproductLoadsInTransitByDestination()
      }
      if (widgetSet.someWidgetShowing(ticketsReadyForPickupWidgets)) {
        this.ticketsReadyForPickup = await this.fetchByproductTicketsReadyForPickup(this.readyForPickupFilter)
      }
      if (widgetSet.someWidgetShowing(incompleteTickets)) {
        await this.fetchIncompleteTickets([
          ContractType.ByproductSale.value,
          ContractType.ByproductPurchase.value,
          ContractType.ByproductHauling.value
        ])
      }
      this.widgetSet = widgetSet
    },

    toggleDenseCharts () {
      this.denseCharts = !this.denseCharts
    },

    addNewWidget () {
      this.widgetPickerMenuKey += 1
    },

    onDataClicked (widget, detailedWidget, pfk, sfk, data, dataObject) {
      this.$emit('data-clicked', {
        widget: widget,
        data: { ...data, paused: dataObject?.paused },
        primaryFilterKey: pfk,
        secondaryFilterKey: sfk,
        primaryFilterValue: dataObject?.xAxisValue,
        secondaryFilterValue: dataObject?.yAxisValue,
        userSettingKey: detailedWidget?.userSettingKey
      })
    },

    isCard (widget) {
      return widget.details?.widgetType?.value === 0 || widget.widgetType?.value === 0
    },

    async refreshChartData (type, options) {
      switch (type) {
        case 'TicketsReadyForPickup':
          this.readyForPickupFilter = { ...options }
          this.ticketsReadyForPickup = await this.fetchByproductTicketsReadyForPickup(options)
          break
        case 'LoadsInTransitByDestination':
          this.loadsInTransitByDestination = await this.fetchByproductLoadsInTransitByDestination()
          break
      }
    },

    widgetMappingByWidgetId (widgetId) {
      return byproductOperationalWidgetMappings.find(i => i.widget.label === widgetId)
    }
  }
}
</script>

<style scoped>
.v-card--link:focus:before { /* This prevents clicked cards from staying grey indefinitely */
  opacity: 0;
}

.v-card--link:hover { /* Vuetify's native "hover" class keeps the element hovered even after you click and move your cursor away. This uses Vuetify elevation-6's style but avoids that bug */
  box-shadow: 0px 3px 5px -1px
    var(--v-shadow-key-umbra-opacity,
    rgba(0, 0, 0, 0.2)), 0px 6px 10px 0px
    var(--v-shadow-key-penumbra-opacity,
    rgba(0, 0, 0, 0.14)), 0px 1px 18px 0px
    var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12)) !important;
  transition: all 0.2s;
}

.v-card--link {
  transition: all 0.2s;
}
</style>
